import {
  getAdminV1MeNotifications,
  getAdminV1StoresStoreIdOrdersIdWorkflow,
  getAdminV1StoresStoreIdProduction,
  patchAdminV1StoresStoreIdOrdersIdWorkflowStagesKey,
  postAdminV1MeNotificationsIdRead,
  postAdminV1MeNotificationsReadAll,
  postAdminV1StoresStoreIdOrdersIdChangeRequests,
  postAdminV1StoresStoreIdOrdersIdChangeRequestsRequestIdResolve,
  postAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyComplete,
  postAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyEnable,
  postAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyParcels,
  postAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyQc,
  postAdminV1StoresStoreIdOrdersIdWorkflowStagesKeySend,
  postAdminV1StoresStoreIdOrdersIdWorkflowStagesKeySkip,
  postAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyStart,
  putAdminV1StoresStoreIdOrdersIdWorkflow,
  type ChangeRequest,
  type GetAdminV1MeNotificationsParams,
  type GetAdminV1StoresStoreIdProduction200,
  type GetAdminV1StoresStoreIdProductionParams,
  type OrderWorkflow,
  type PatchAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyBody,
  type PostAdminV1StoresStoreIdOrdersIdChangeRequestsBody,
  type PostAdminV1StoresStoreIdOrdersIdChangeRequestsRequestIdResolveBody,
  type PostAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyCompleteBody,
  type PostAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyParcelsBody,
  type PostAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyQcBody,
  type PostAdminV1StoresStoreIdOrdersIdWorkflowStagesKeySendBody,
  type PostAdminV1StoresStoreIdOrdersIdWorkflowStagesKeySkipBody,
  type StaffNotification,
  type StaffNotificationPage,
} from '@nucleus-commerce/api-client/admin';

export interface StageRef {
  storeId: string;
  orderId: string;
  key: string;
}

const stagePath = ({ storeId, orderId, key }: StageRef) => ({ storeId, id: orderId, key });

/** Every stage action answers with the whole workflow, so callers replace their cache with it. */
export const productionApi = {
  workflow: (storeId: string, orderId: string): Promise<OrderWorkflow> =>
    getAdminV1StoresStoreIdOrdersIdWorkflow({ storeId, id: orderId }),
  switchTemplate: (storeId: string, orderId: string, templateId: string): Promise<OrderWorkflow> =>
    putAdminV1StoresStoreIdOrdersIdWorkflow({ storeId, id: orderId }, { templateId }),
  start: (ref: StageRef): Promise<OrderWorkflow> =>
    postAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyStart(stagePath(ref)),
  complete: (
    ref: StageRef,
    body: PostAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyCompleteBody,
  ): Promise<OrderWorkflow> =>
    postAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyComplete(stagePath(ref), body),
  skip: (
    ref: StageRef,
    body: PostAdminV1StoresStoreIdOrdersIdWorkflowStagesKeySkipBody,
  ): Promise<OrderWorkflow> =>
    postAdminV1StoresStoreIdOrdersIdWorkflowStagesKeySkip(stagePath(ref), body),
  enable: (ref: StageRef): Promise<OrderWorkflow> =>
    postAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyEnable(stagePath(ref)),
  update: (
    ref: StageRef,
    body: PatchAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyBody,
  ): Promise<OrderWorkflow> =>
    patchAdminV1StoresStoreIdOrdersIdWorkflowStagesKey(stagePath(ref), body),
  submitQc: (
    ref: StageRef,
    body: PostAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyQcBody,
  ): Promise<OrderWorkflow> =>
    postAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyQc(stagePath(ref), body),
  send: (
    ref: StageRef,
    body: PostAdminV1StoresStoreIdOrdersIdWorkflowStagesKeySendBody,
  ): Promise<OrderWorkflow> =>
    postAdminV1StoresStoreIdOrdersIdWorkflowStagesKeySend(stagePath(ref), body),
  parcels: (
    ref: StageRef,
    body: PostAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyParcelsBody,
  ): Promise<OrderWorkflow> =>
    postAdminV1StoresStoreIdOrdersIdWorkflowStagesKeyParcels(stagePath(ref), body),
  createChangeRequest: (
    storeId: string,
    orderId: string,
    body: PostAdminV1StoresStoreIdOrdersIdChangeRequestsBody,
  ): Promise<ChangeRequest> =>
    postAdminV1StoresStoreIdOrdersIdChangeRequests({ storeId, id: orderId }, body),
  resolveChangeRequest: (
    storeId: string,
    orderId: string,
    requestId: string,
    body: PostAdminV1StoresStoreIdOrdersIdChangeRequestsRequestIdResolveBody,
  ): Promise<ChangeRequest> =>
    postAdminV1StoresStoreIdOrdersIdChangeRequestsRequestIdResolve(
      { storeId, id: orderId, requestId },
      body,
    ),
  board: (
    storeId: string,
    params: GetAdminV1StoresStoreIdProductionParams,
  ): Promise<GetAdminV1StoresStoreIdProduction200> =>
    getAdminV1StoresStoreIdProduction({ storeId }, params),
};

/** The signed-in member's in-app notifications; not store-scoped. */
export const staffNotificationsApi = {
  list: (params: GetAdminV1MeNotificationsParams): Promise<StaffNotificationPage> =>
    getAdminV1MeNotifications(params),
  read: (id: string): Promise<StaffNotification> => postAdminV1MeNotificationsIdRead({ id }),
  readAll: (): Promise<{ updated: number }> => postAdminV1MeNotificationsReadAll(),
};
